import React, { useState, useEffect } from 'react';
import {
    Button,
    message,
    Card,
    List,
    Avatar,
    Tag,
    Popconfirm,
    Typography,
    Space,
    Empty,
    Alert,
    Spin,
    Tooltip
} from 'antd';
import { 
    LinkOutlined, 
    DisconnectOutlined, 
    ExclamationCircleOutlined,
    CheckCircleOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { nanoid } from 'nanoid';
import useRequest from '@/utils/useRequest';
import { useCommonStore } from '@/store/useCommonStore';
import logo_guanshi from '../../../public/asset/images/logo.svg';
import logo_wechat from '../../../public/asset/images/logo_wechat.svg';
import logo_qq from '../../../public/asset/images/logo_qq.svg';
import logo_weibo from '../../../public/asset/images/logo_webo.svg';

const initParams = {
  pagination: {
    current: 1,
    pageSize: 10
  },
  filters: {
    userType: 0,
  },
  sorter: null
};


const AccountBindingTab = ({ user }) => {
    const { request } = useRequest();
    
    const [loading, setLoading] = useState(false);
    const [bindingAccounts, setBindingAccounts] = useState([]);
    const [refreshLoading, setRefreshLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    
    // OAuth 提供商配置
    const oauthProviders = {
        'git.wsc.tech': {
            name: '贯石Git',
            icon: <img src={logo_guanshi} alt="贯石Git" className="w-6 h-6" />,
            color: 'green',
            description: '贯石Git平台账号',
            authUrl: 'https://git.wsc.tech/oauth/authorize?client_id=9db5bfc177c993c6487d3a6d74e196e59f28003d53b2783955054a411a0e136b&redirect_uri=' + encodeURIComponent(window.location.protocol + '//' + window.location.host + '/auth/bind?state=') + '${state}&response_type=code&scope=openid%20api'
        },
        'wechat': {
            name: '微信',
            icon: <img src={logo_wechat} alt="微信" className="w-6 h-6" />,
            color: 'green',
            description: '使用微信账号快速登录',
            authUrl: '/api/v2/oauth/wechat/bind?state=${state}'
        },
        'qq': {
            name: 'QQ',
            icon: <img src={logo_qq} alt="QQ" className="w-6 h-6" />,
            color: 'blue',
            description: '使用QQ账号快速登录',
            authUrl: '/api/v2/oauth/qq/bind?state=${state}'
        },
        'weibo': {
            name: '微博',
            icon: <img src={logo_weibo} alt="微博" className="w-6 h-6" />,
            color: 'red',
            description: '使用微博账号快速登录',
            authUrl: '/api/v2/oauth/weibo/bind?state=${state}'
        },
        'github': {
            name: 'GitHub',
            icon: '⚫',
            color: 'default',
            description: '使用GitHub账号快速登录',
            authUrl: '/api/v2/oauth/github/bind?state=${state}'
        },
        'google': {
            name: 'Google',
            icon: '🔵',
            color: 'blue',
            description: '使用Google账号快速登录',
            authUrl: '/api/v2/oauth/google/bind?state=${state}'
        },
        'alipay': {
            name: '支付宝',
            icon: '🔵',
            color: 'cyan',
            description: '使用支付宝账号快速登录',
            authUrl: '/api/v2/oauth/alipay/bind?state=${state}'
        }
    };
    
    // 查询已绑定的账户信息
    const fetchBindingAccounts = async (showMessage = false) => {
        setRefreshLoading(true);
        setFetchError(null);
        try {
            const result = await request('/api/v2/private/auth/search', 'GET', 'json');
            
            if (result.success && result.data) {
                // 根据API返回格式，data直接是绑定账户数组
                // 需要将API返回的字段映射到组件期望的格式
                const mappedAccounts = result.data.map(account => ({
                    Id: account.id,
                    Provider: account.source, // 使用source作为provider
                    Username: account.sourceName || account.source,
                    Email: '', // API返回中没有email字段
                    BindTime: account.createTime,
                    IsActive: account.isActive === 't'
                }));
                
                setBindingAccounts(mappedAccounts);
                if (showMessage) {
                    message.success('绑定信息已更新');
                }
            } else {
                console.warn('获取绑定账户信息失败:', result.message);
                setFetchError(result.message || '获取绑定信息失败');
                // 如果接口失败，尝试使用传入的user数据作为备选
                if (user && user.OAuthAccounts) {
                    setBindingAccounts(user.OAuthAccounts);
                }
            }
        } catch (err) {
            console.error('查询绑定账户错误:', err);
            const errorMsg = '获取绑定信息失败，请重试';
            setFetchError(errorMsg);
            if (showMessage) {
                message.error(errorMsg);
            }
            // 如果接口失败，尝试使用传入的user数据作为备选
            if (user && user.OAuthAccounts) {
                setBindingAccounts(user.OAuthAccounts);
            }
        } finally {
            setRefreshLoading(false);
        }
    };

    // 检查绑定结果（从URL参数或sessionStorage）
    const checkBindingResult = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const bindSuccess = urlParams.get('bind_success');
        const bindError = urlParams.get('bind_error');
        
        if (bindSuccess === 'true') {
            const provider = sessionStorage.getItem('bind_provider');
            message.success(`${provider ? oauthProviders[provider]?.name || provider : '账户'}绑定成功！`);
            sessionStorage.removeItem('bind_provider');
            // 清除URL参数
            window.history.replaceState({}, document.title, window.location.pathname);
            // 刷新绑定信息
            fetchBindingAccounts();
        } else if (bindError) {
            message.error(`绑定失败: ${decodeURIComponent(bindError)}`);
            sessionStorage.removeItem('bind_provider');
            // 清除URL参数
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    };

    // 初始化绑定账户信息
    useEffect(() => {
        fetchBindingAccounts();
        checkBindingResult();
    }, [user]);

    // 监听页面焦点，当从OAuth页面返回时刷新数据
    useEffect(() => {
        const handleFocus = () => {
            // 检查是否有待处理的绑定状态
            const bindProvider = sessionStorage.getItem('bind_provider');
            if (bindProvider) {
                // 延迟一下再检查，给OAuth回调处理一些时间
                setTimeout(() => {
                    fetchBindingAccounts();
                }, 1000);
            }
        };

        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, []);
    
    // 绑定第三方账户
    const bindAccount = async (provider) => {
        setLoading(true);
        try {
            // 获取对应提供商的授权URL
            const providerConfig = oauthProviders[provider];
            if (!providerConfig || !providerConfig.authUrl) {
                throw new Error(`未找到提供商 ${provider} 的配置`);
            }
            
            // 生成随机 state 并存储到 sessionStorage
            const state = nanoid();
            sessionStorage.setItem("auth_state", state);
            sessionStorage.setItem("bind_provider", provider);
            
            // 替换 URL 模板中的 state 占位符
            const authUrl = providerConfig.authUrl.replace('${state}', state);
            
            // 跳转到OAuth授权页面
            window.location.href = authUrl;
        } catch (err) {
            console.error('绑定账户错误:', err);
            message.error(err.message || '绑定失败，请重试');
        } finally {
            setLoading(false);
        }
    };
    
    // 解绑第三方账户
    const unbindAccount = async (accountId, provider) => {
        setLoading(true);
        try {
            // 根据API文档，可以传入socialUserId或source参数
            const requestData = {};
            if (accountId) {
                requestData.socialUserId = parseInt(accountId);
            }
            if (provider) {
                requestData.source = provider;
            }
            
            const result = await request('/api/v2/private/auth/unbind', 'POST', 'json', requestData);
            
            if (result.success) {
                message.success(`${oauthProviders[provider]?.name || provider}账户解绑成功`);
                // 解绑成功后重新获取最新的绑定信息
                await fetchBindingAccounts();
            } else {
                message.error(result.message || '解绑失败，请重试');
            }
        } catch (err) {
            console.error('解绑账户错误:', err);
            message.error('解绑失败，请重试');
        } finally {
            setLoading(false);
        }
    };
    
    // 获取已绑定的提供商列表
    const getBoundProviders = () => {
        return bindingAccounts.map(account => account.Provider);
    };
    
    // 渲染绑定账户项
    const renderBoundAccount = (account) => {
        const provider = oauthProviders[account.Provider];
        if (!provider) return null;
        
        return (
            <Card size="small" className="mb-4" key={account.Id}>
                <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                        <span className="text-2xl">{provider.icon}</span>
                        <div>
                            <div className="flex items-center space-x-2">
                                <span className="font-medium">{provider.name}</span>
                                <Tag color={provider.color} icon={<CheckCircleOutlined />}>
                                    已绑定
                                </Tag>
                            </div>
                            <div className="text-sm text-gray-500">
                                {account.Username || account.Email || '未知账户'}
                            </div>
                            <div className="text-xs text-gray-400">
                                绑定时间: {dayjs(account.BindTime).format('YYYY-MM-DD HH:mm')}
                            </div>
                        </div>
                    </div>
                    <Popconfirm
                        title={`确定要解绑${provider.name}账户吗？`}
                        description="解绑后将无法使用该账户登录"
                        onConfirm={() => unbindAccount(account.Id, account.Provider)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Tooltip title="解绑账户">
                            <Button
                                type="text"
                                danger
                                icon={<DisconnectOutlined />}
                                loading={loading}
                            >
                                解绑
                            </Button>
                        </Tooltip>
                    </Popconfirm>
                </div>
            </Card>
        );
    };
    
    // 渲染可绑定的提供商
    const renderAvailableProvider = (providerKey, provider) => {
        const boundProviders = getBoundProviders();
        const isBound = boundProviders.includes(providerKey);
        
        if (isBound) return null;
        
        return (
            <Card size="small" className="mb-4" key={providerKey}>
                <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                        <span className="text-2xl">{provider.icon}</span>
                        <div>
                            <div className="font-medium">{provider.name}</div>
                            <div className="text-sm text-gray-500">{provider.description}</div>
                        </div>
                    </div>
                    <Button
                        type="primary"
                        icon={<LinkOutlined />}
                        onClick={() => bindAccount(providerKey)}
                        loading={loading}
                    >
                        绑定
                    </Button>
                </div>
            </Card>
        );
    };
    
    const boundProviders = getBoundProviders();
    const availableProviders = Object.entries(oauthProviders).filter(
        ([key]) => !boundProviders.includes(key)
    );
    
    return (
        <Card>
            <div className="max-w-2xl mx-auto">
                <div className="text-center mb-6">
                    <LinkOutlined className="text-4xl text-purple-500 mb-2" />
                    <div className="flex items-center justify-center space-x-2 mb-2">
                        <h3 className="text-lg font-semibold">第三方账户绑定</h3>
                        <Tooltip title="刷新绑定信息">
                            <Button
                                type="text"
                                size="small"
                                icon={<ReloadOutlined />}
                                loading={refreshLoading}
                                onClick={() => fetchBindingAccounts(true)}
                            />
                        </Tooltip>
                    </div>
                    <p className="text-gray-500 text-sm">绑定第三方账户后，可以使用这些账户快速登录</p>
                </div>
                
                <Alert
                    message="安全提示"
                    description="绑定第三方账户可以提供更便捷的登录方式，但请确保第三方账户的安全性"
                    type="info"
                    showIcon
                    className="mb-6"
                />

                {/* 加载状态 */}
                {refreshLoading && (
                    <div className="text-center py-8">
                        <Spin size="large" />
                        <p className="text-gray-500 mt-4">正在获取绑定信息...</p>
                    </div>
                )}

                {/* 错误状态 */}
                {!refreshLoading && fetchError && (
                    <Alert
                        message="获取绑定信息失败"
                        description={fetchError}
                        type="error"
                        showIcon
                        action={
                            <Button
                                size="small"
                                type="primary"
                                onClick={() => fetchBindingAccounts(true)}
                            >
                                重试
                            </Button>
                        }
                        className="mb-6"
                    />
                )}
                
                {/* 已绑定账户 */}
                {!refreshLoading && bindingAccounts.length > 0 && (
                    <div className="mb-8">
                        <h4 className="text-md font-medium mb-4 flex items-center">
                            <CheckCircleOutlined className="text-green-500 mr-2" />
                            已绑定账户 ({bindingAccounts.length})
                        </h4>
                        {bindingAccounts.map(renderBoundAccount)}
                    </div>
                )}
                
                {/* 可绑定的账户 */}
                {!refreshLoading && availableProviders.length > 0 && (
                    <div className="mb-6">
                        <h4 className="text-md font-medium mb-4 flex items-center">
                            <ExclamationCircleOutlined className="text-orange-500 mr-2" />
                            可绑定账户 ({availableProviders.length})
                        </h4>
                        {availableProviders.map(([key, provider]) => 
                            renderAvailableProvider(key, provider)
                        )}
                    </div>
                )}
                
                {/* 全部绑定完成 */}
                {!refreshLoading && availableProviders.length === 0 && bindingAccounts.length > 0 && (
                    <div className="text-center py-8">
                        <CheckCircleOutlined className="text-4xl text-green-500 mb-2" />
                        <p className="text-gray-500">您已绑定所有支持的第三方账户</p>
                    </div>
                )}
                
                {/* 没有任何绑定 */}
                {!refreshLoading && bindingAccounts.length === 0 && (
                    <Empty
                        description="暂未绑定任何第三方账户"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    >
                        <p className="text-gray-500 mb-4">绑定第三方账户可以让您更方便地登录</p>
                    </Empty>
                )}
                
                {!refreshLoading && (
                    <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                            <ExclamationCircleOutlined className="mr-1" />
                            绑定说明
                        </h4>
                        <ul className="text-xs text-gray-600 space-y-1">
                            <li>• 绑定第三方账户后，可以使用该账户直接登录，无需输入用户名密码</li>
                            <li>• 每个第三方平台只能绑定一个账户</li>
                            <li>• 解绑账户不会影响您的主账户，但会失去快速登录功能</li>
                            <li>• 建议至少保留一种登录方式，避免无法登录的情况</li>
                            <li>• 绑定过程中会跳转到第三方平台进行授权确认</li>
                            <li>• 如果绑定信息未及时更新，请点击右上角的刷新按钮</li>
                        </ul>
                    </div>
                )}
            </div>
        </Card>
    );
};

export default AccountBindingTab;